import { Value } from 'baseui/select';
import { useFormState } from 'components/elements/form';
import deleteColumn from 'components/elements/table/editable-columns/delete-column';
import TableInput from 'components/elements/table/table-input';
import {
  ActionType,
  ColumnDataType,
  IColumn,
} from 'components/elements/table/type';
import useGetProductVariantColumn from 'hooks/use-get-product-variant-column';
import { SeparatorVertical } from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';

import { onAddNewProduct } from '../../hooks/use-get-default-values';
import { ProductSaleDetailFormType, ProductSaleFormType } from '../form-type';

function useGetProductVariantList(): IColumn<ProductSaleDetailFormType>[] {
  const { t } = useTranslation();
  const { editable } = useFormState();
  const productVariantColumn = useGetProductVariantColumn(true);
  return [
    {
      header: t('product:product-variant'),
      accessorKey: 'productVariantId',
      minSize: 400,
      dataType: ColumnDataType.select,
      required: true,
      selectProps: productVariantColumn,
    },
    {
      header: t('common:stock'),
      accessorKey: 'stock',
      minSize: 100,
      dataType: ColumnDataType.number,
      minNumber: 0,
      disabled: true,
    },
    {
      header: t('product:price_per_item'),
      accessorKey: 'price',
      minSize: 100,
      dataType: ColumnDataType.number,
      minNumber: 0,
      required: true,
    },
    {
      header: t('common:quantity'),
      accessorKey: 'qty',
      minSize: 100,
      dataType: ColumnDataType.number,
      minNumber: 0,
      required: true,
      maxNumberKey: 'stock',
    },
    {
      header: t('common:description'),
      accessorKey: 'description',
      minSize: 500,
      dataType: ColumnDataType.text,
    },

    ...deleteColumn(editable),
  ];
}
export default function ProductVariantTable() {
  const { editable } = useFormState();

  const { control, clearErrors, getValues, trigger } =
    useFormContext<ProductSaleFormType>();

  const columns = useGetProductVariantList();

  const name = 'productSales' as const;

  const { fields, update, remove, append } = useFieldArray({
    control,
    name,
    keyName: '__customId',
  });

  const dispatch = React.useCallback(
    (action) => {
      const products = getValues(name);
      const currentRowIndex = action.rowIndex as number;
      const product = products[currentRowIndex];
      const type = action.type as ActionType;
      const columnId = action.columnId as keyof ProductSaleDetailFormType;
      const value = action.value;
      const extra = action.extra as Value;

      const otherVal: () => Partial<ProductSaleDetailFormType> = () => {
        if (columnId === 'productVariantId') {
          const price = extra?.[0]?.extra?.sellPrice || 0;
          const qty = product.qty || 0;

          return {
            stock: extra?.[0]?.extra?.stock,
            price,
            qty,
          };
        }
        return {};
      };

      if (type === 'addRow') {
        append(onAddNewProduct());
        trigger(name);
      } else if (type === 'removeRow') {
        remove(currentRowIndex);
        trigger(name);
      } else if (type === 'update_cell') {
        update(currentRowIndex, {
          ...product,
          [columnId]: value,
          ...otherVal(),
        });
        clearErrors(`${name}.${currentRowIndex}.${columnId}`);
      }
    },
    [append, clearErrors, getValues, name, remove, trigger, update],
  );

  return (
    <div style={{ width: '100%' }}>
      <SeparatorVertical $margin={36} />
      <TableInput
        columns={columns}
        data={fields}
        dispatch={dispatch}
        disabled={!editable}
        name={name}
        uniqueRowKey="customId"
      />
    </div>
  );
}
